import React, { useState, useEffect } from 'react';
import { Trophy, TrendingUp, Clock, Users, AlertTriangle, Settings, Loader2, Save, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { ContextualTour } from '../../components/ContextualTour';
import { API } from '../../config/apiBase';

const MEDALS = { ouro: { color: 'var(--sv-accent)', label: '1o' }, prata: { color: '#C0C0C0', label: '2o' }, bronze: { color: '#CD7F32', label: '3o' } };
const DAYS = ['Domingo', 'Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'];
const CHART_COLORS = ['#2DD4BF', '#60A5FA', '#34D399', '#F472B6', '#A78BFA', '#FB923C', '#22D3EE', '#E879F9'];

const RANKING_STEPS = [
    { target: '[data-testid="ranking-chart-bar"]', title: 'Grafico de Scores', content: 'Compare visualmente o desempenho de cada unidade nesta semana.' },
    { target: '[data-testid="ranking-chart-evolution"]', title: 'Evolucao Semanal', content: 'Acompanhe a evolucao dos scores ao longo das semanas. Identifique tendencias.' },
    { target: '[data-testid="ranking-cards"]', title: 'Detalhes por Unidade', content: 'Veja presenca, pontualidade, consistencia e alertas de cada unidade.' },
];

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: '#0D2C63', border: '1px solid rgba(140,180,255,0.30)', borderRadius: 8, padding: '8px 12px', fontSize: 11 }}>
            <p style={{ color: '#999', marginBottom: 4 }}>{label}</p>
            {payload.map((p, i) => (
                <p key={i} style={{ color: p.color }}>{p.name}: <strong>{p.value} pts</strong></p>
            ))}
        </div>
    );
}

export default function EmpresaRankingPage() {
    const [ranking, setRanking] = useState([]);
    const [periodo, setPeriodo] = useState('');
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [showConfig, setShowConfig] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    useEffect(() => {
        Promise.all([
            fetch(`${API}/enterprise/ranking-unidades`, { headers }).then(r => r.ok ? r.json() : { ranking: [] }),
            fetch(`${API}/enterprise/ranking-config`, { headers }).then(r => r.ok ? r.json() : null),
            fetch(`${API}/enterprise/ranking-history?weeks=8`, { headers }).then(r => r.ok ? r.json() : { history: [] }),
        ]).then(([rankData, cfgData, histData]) => {
            setRanking(rankData.ranking || []);
            setPeriodo(rankData.periodo || '');
            setConfig(cfgData || { enabled: false, day_of_week: 1, hour: 9, channels: ['telegram'], metrics: ['presenca', 'pontualidade', 'consistencia', 'alertas'], chat_id: '' });
            setHistory(histData.history || []);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const handleSaveConfig = async () => {
        setSaving(true);
        try {
            const r = await fetch(`${API}/enterprise/ranking-config`, { method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(config) });
            if (r.ok) toast.success('Configuracao salva!');
            else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexao'); }
        setSaving(false);
    };

    const handleTest = async () => {
        setTesting(true);
        try {
            const r = await fetch(`${API}/enterprise/ranking-unidades/test`, { method: 'POST', headers });
            const data = await r.json();
            if (r.ok) {
                setRanking(data.ranking || []);
                toast.success('Ranking calculado!');
                // Refresh history
                const hist = await fetch(`${API}/enterprise/ranking-history?weeks=8`, { headers }).then(r => r.ok ? r.json() : { history: [] });
                setHistory(hist.history || []);
            } else toast.error(data.detail || 'Erro');
        } catch { toast.error('Erro de conexao'); }
        setTesting(false);
    };

    const toggleChannel = (ch) => {
        const channels = config.channels || [];
        setConfig({ ...config, channels: channels.includes(ch) ? channels.filter(c => c !== ch) : [...channels, ch] });
    };
    const toggleMetric = (m) => {
        const metrics = config.metrics || [];
        setConfig({ ...config, metrics: metrics.includes(m) ? metrics.filter(x => x !== m) : [...metrics, m] });
    };

    // Prepare chart data
    const barData = ranking.map(r => ({ name: r.name?.substring(0, 12) || 'N/A', score: r.score, presenca: r.presenca, pontualidade: r.pontualidade }));

    const evolutionData = history.map(h => {
        const date = h.timestamp ? new Date(h.timestamp).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '';
        const row = { semana: date };
        (h.ranking || []).forEach(r => { row[r.name] = r.score; });
        return row;
    });
    const unitNames = ranking.map(r => r.name);

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div className="space-y-5" data-testid="ranking-page">
            <ContextualTour pageKey="ranking" steps={RANKING_STEPS} />

            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h1 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Ranking de Unidades</h1>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{periodo || 'Ranking semanal entre suas unidades'}</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={handleTest} disabled={testing} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="test-ranking-btn">
                        {testing ? <Loader2 size={12} className="animate-spin" /> : <TrendingUp size={12} />} Calcular Agora
                    </button>
                    <button onClick={() => setShowConfig(!showConfig)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--muted)' }} data-testid="toggle-ranking-config">
                        <Settings size={12} /> Config
                    </button>
                </div>
            </div>

            {/* Config Panel */}
            {showConfig && config && (
                <div className="rounded-xl p-4 space-y-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="ranking-config-panel">
                    <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={config.enabled} onChange={e => setConfig({ ...config, enabled: e.target.checked })} className="accent-[var(--sv-accent)]" />
                            <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>Envio automatico ativado</span>
                        </label>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Dia da semana</label>
                            <select value={config.day_of_week} onChange={e => setConfig({ ...config, day_of_week: parseInt(e.target.value) })} className="w-full rounded-lg px-2 py-1.5 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Horario (UTC)</label>
                            <select value={config.hour} onChange={e => setConfig({ ...config, hour: parseInt(e.target.value) })} className="w-full rounded-lg px-2 py-1.5 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                                {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Chat ID Telegram</label>
                            <input type="text" value={config.chat_id} onChange={e => setConfig({ ...config, chat_id: e.target.value })} placeholder="Opcional" className="w-full rounded-lg px-2 py-1.5 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                        </div>
                        <div>
                            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Canais</label>
                            <div className="flex gap-2">
                                {['telegram', 'whatsapp'].map(ch => (
                                    <label key={ch} className="flex items-center gap-1 cursor-pointer">
                                        <input type="checkbox" checked={(config.channels || []).includes(ch)} onChange={() => toggleChannel(ch)} className="accent-[var(--sv-accent)]" />
                                        <span className="text-[10px] capitalize" style={{ color: 'var(--text)' }}>{ch}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Metricas</label>
                        <div className="flex gap-3 flex-wrap">
                            {[{ key: 'presenca', label: 'Presenca' }, { key: 'pontualidade', label: 'Pontualidade' }, { key: 'consistencia', label: 'Consistencia' }, { key: 'alertas', label: 'Alertas' }].map(m => (
                                <label key={m.key} className="flex items-center gap-1 cursor-pointer">
                                    <input type="checkbox" checked={(config.metrics || []).includes(m.key)} onChange={() => toggleMetric(m.key)} className="accent-[var(--sv-accent)]" />
                                    <span className="text-[10px]" style={{ color: 'var(--text)' }}>{m.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <button onClick={handleSaveConfig} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="save-ranking-config">
                        {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Salvar
                    </button>
                </div>
            )}

            {/* Charts */}
            {ranking.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Bar Chart - Current Scores */}
                    <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="ranking-chart-bar">
                        <div className="flex items-center gap-2 mb-3">
                            <BarChart3 size={14} style={{ color: 'var(--sv-accent)' }} />
                            <h3 className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Score Atual por Unidade</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={barData} margin={{ top: 5, right: 5, bottom: 5, left: -15 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#888' }} />
                                <YAxis tick={{ fontSize: 10, fill: '#888' }} domain={[0, 100]} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="score" fill="#4E8DF0" radius={[4, 4, 0, 0]} name="Score" />
                                <Bar dataKey="presenca" fill="#34D399" radius={[4, 4, 0, 0]} name="Presenca" />
                                <Bar dataKey="pontualidade" fill="#60A5FA" radius={[4, 4, 0, 0]} name="Pontualidade" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Line Chart - Evolution */}
                    <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="ranking-chart-evolution">
                        <div className="flex items-center gap-2 mb-3">
                            <TrendingUp size={14} style={{ color: 'var(--sv-accent)' }} />
                            <h3 className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Evolucao Semanal</h3>
                        </div>
                        {evolutionData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={200}>
                                <LineChart data={evolutionData} margin={{ top: 5, right: 5, bottom: 5, left: -15 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                    <XAxis dataKey="semana" tick={{ fontSize: 10, fill: '#888' }} />
                                    <YAxis tick={{ fontSize: 10, fill: '#888' }} domain={[0, 100]} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ fontSize: 10, color: '#888' }} />
                                    {unitNames.map((name, i) => (
                                        <Line key={name} type="monotone" dataKey={name} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} name={name} />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-[200px]">
                                <p className="text-xs" style={{ color: 'var(--muted)' }}>Clique em "Calcular Agora" para gerar historico</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Ranking Cards */}
            {ranking.length === 0 ? (
                <div className="rounded-xl p-8 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <Trophy size={32} className="mx-auto mb-3" style={{ color: 'var(--muted-2)' }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Sem dados de ranking</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Clique em "Calcular Agora" para gerar o ranking semanal.</p>
                </div>
            ) : (
                <div className="space-y-3" data-testid="ranking-cards">
                    {ranking.map((r) => {
                        const medal = MEDALS[r.medalha];
                        return (
                            <div key={r.unidade_id} className="rounded-xl p-4 transition-all hover:scale-[1.005]" style={{ background: 'var(--surface)', border: `1px solid ${medal ? medal.color + '40' : 'var(--border)'}` }} data-testid={`ranking-card-${r.posicao}`}>
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 font-bold text-lg" style={{ background: medal ? medal.color + '15' : 'var(--surface-2)', color: medal ? medal.color : 'var(--muted)', fontFamily: "var(--font-display)" }}>
                                        {medal ? medal.label : `${r.posicao}o`}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{r.name}</span>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: r.score >= 70 ? 'rgba(16,185,129,0.1)' : r.score >= 40 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)', color: r.score >= 70 ? '#10B981' : r.score >= 40 ? '#F59E0B' : '#EF4444' }}>
                                                {r.score} pts
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                                            <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--muted)' }}><Users size={10} /> {r.total_colabs} colab.</span>
                                            <span className="flex items-center gap-1 text-[10px]" style={{ color: r.presenca >= 60 ? '#10B981' : '#EF4444' }}><TrendingUp size={10} /> Presenca: {r.presenca}%</span>
                                            <span className="flex items-center gap-1 text-[10px]" style={{ color: r.pontualidade >= 80 ? '#10B981' : '#F59E0B' }}><Clock size={10} /> Pontualidade: {r.pontualidade}%</span>
                                            <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--muted)' }}><AlertTriangle size={10} /> Alertas: {r.alertas_semana}</span>
                                        </div>
                                    </div>
                                    <div className="w-24 shrink-0 hidden sm:block">
                                        <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                                            <div className="h-full rounded-full transition-all" style={{ width: `${r.score}%`, background: r.score >= 70 ? '#10B981' : r.score >= 40 ? '#F59E0B' : '#EF4444' }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
